import React, { useState, useMemo } from 'react';
import { compose } from 'redux';

import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import ListSearch from 'components/ListSearch';
import List from 'scenes/List';
import InboundsCandidateItem from 'scenes/List/components/InboundsCandidateItem';
import FiltersAccordion from 'scenes/FiltersAccordion';
import FiltersWidget from 'scenes/FiltersAccordion/components/FiltersWidget';
import BasicNavLinkButton from 'components/BasicNavLinkButton';
import GridHistoryWithFilters from 'components/GridHistoryWithFilters';
import ListSearchMinimalContentLoader from 'components/ListSearchMinimalContentLoader';
import FiltersContentLoader from 'components/FiltersContentLoader';
import HorizontalLoader from 'components/HorizontalLoader';
import NoEventsMask from 'components/NoEventsMask';
import WidgetOnMobile from 'scenes/Sidebar/components/WidgetOnMobile';
import MediaQuery from 'react-responsive';
import FiltersMobileToggler from 'scenes/Sidebar/components/FiltersMobileToggler';

import withTakeActions from 'hocs/withTakeActions';
import withSearchActions from 'hocs/withSearchActions';
import { useGrid } from 'hooks/useGrid';
import { updateHistory } from 'helpers/grid-history';
import { useGridFilters } from 'hooks/useGridFilters';
import { useSelection } from 'hooks/useSelection';
import { Permission, Role } from 'helpers/user';
import { MESSAGE_TYPE } from 'constants/message-types';
import { ENDPOINT } from 'constants/endpoints';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

import './styles.css';

const InBounds = props => {
  const {
    user,
    history,
    location: { pathname },
    searchAction: {
      messageSelected,
      message,
      massSendJob,
      downloadResumeInbounds,
    },
    takeAction,
  } = props;

  const [mobileFilters, toggleMobileFilters] = useState(false);

  const isAllowedMassSendJob = useMemo(
    () => Permission.isAllowedMassSendJob(user.permissions),
    [user]
  );

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const filters = useGridFilters(ENDPOINT.FILTERS.INBOUNDS);

  const grid = useGrid(ENDPOINT.COLLECTION.INBOUNDS, params => {
    updateHistory(history, params);
  });

  const [
    { isSelected, selectAll, deselectAll, select, deselect, getSelectedIds },
  ] = useSelection(grid.data.ids);

  const filtersAccordion = useMemo(
    () =>
      filters.data && (
        <FiltersAccordion
          title="Filters"
          subTitle="Use the search Filters to narrow your candidate search."
          filters={filters.data}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          render={renderProps => <FiltersWidget {...renderProps} />}
          renderHeader={
            <MediaQuery query="(max-width: 767px)">
              <FiltersMobileToggler
                title="Search Filters:"
                toggleFunc={toggleMobileFiltersHandler}
              />
            </MediaQuery>
          }
        />
      ),
    [filters.data]
  );

  const handleOnSelectAll = checked => {
    checked ? selectAll() : deselectAll();
  };

  const sendMessage = ({ CandidateID, FullName }) => {
    message(CandidateID, FullName, MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const sendReplyMessage = ({ CandidateID, FullName }) => {
    message(CandidateID, FullName, MESSAGE_TYPE.TYPE_INBOUNDS_REPLY, () =>
      grid.actions.fetch()
    );
  };

  const handleRatedCandidate = item => {
    takeAction
      .rate(item)
      .then(() => grid.actions.fetch())
      .catch(() => {});
  };

  const handleDownloadResume = item => {
    takeAction.resume(item);
  };

  const sendMassSetupEmail = () => {
    massSendJob(getSelectedIds());
  };

  const sendMassMessage = () => {
    messageSelected(getSelectedIds(), MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const handleMassDownloadResume = () => {
    downloadResumeInbounds(getSelectedIds(), filters.actions.getQuery());
  };

  const searchActionButtons = [
    {
      caption: 'Send Job',
      controlFunc: sendMassSetupEmail,
      hidden: !isAllowedMassSendJob,
    },
    {
      caption: 'Message',
      controlFunc: sendMassMessage,
    },
    {
      caption: 'Resumes',
      controlFunc: handleMassDownloadResume,
    },
  ];

  const searchActionSelectors = [
    {
      caption: 'Select All',
      icon: 'tick',
      iconActive: 'cross',
      controlFunc: handleOnSelectAll,
    },
  ];

  const itemFooterActions = [
    {
      caption: 'Download Resume',
      icon: 'download',
      handler: handleDownloadResume,
    },
    {
      caption: 'Reply',
      icon: 'message',
      handler: sendReplyMessage,
    },
    {
      caption: 'Message',
      icon: 'message',
      handler: sendMessage,
    },
    {
      caption: 'Rate',
      icon: 'rate',
      handler: handleRatedCandidate,
    },
    {
      caption: 'Tag',
      icon: 'tag',
      hidden: !Role.isRecruiterPremiumRecFirm(user),
      handler: takeAction.tag,
    },
  ];

  const sortControls = [
    {
      defaultCaption: 'Last Login',
      toggledCaption: 'Last Login',
      value: 'default',
    },
    {
      defaultCaption: 'UnReplied',
      toggledCaption: 'UnReplied',
      value: 'sent',
    },
    {
      defaultCaption: 'Replied',
      toggledCaption: 'Replied',
      value: 'replied',
    },
    {
      defaultCaption: 'Rating',
      toggledCaption: 'Rating',
      value: 'rating',
    },
    {
      defaultCaption: 'GAT Score',
      toggledCaption: 'GAT Score',
      value: 'gat',
    },
  ];

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{ sortBy: 'default' }}
    >
      <PageLayout
        pageClass="inbounds-page"
        pageTitle="Inbounds"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                sortBy={grid.data.params.sortBy || ''}
                sortControls={sortControls}
                recordsName={
                  Number(grid.data.rowsCount) === 1 ? 'match' : 'matches'
                }
                recordsCount={grid.data.rowsCount}
                search={grid.data.params.search || false}
                searchPlaceholder="Type your search here"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                actionButtonControls={searchActionButtons}
                actionSelectControls={searchActionSelectors}
                isFetching={grid.data.isFetching}
                renderHeader={
                  <div className="inbounds-page__header">
                    <h2 className="inbounds-page__title">InBounds</h2>
                    <div className="inbounds-page__action">
                      <BasicNavLinkButton
                        to={`${pathname}/requirements`}
                        btnWidth={200}
                        btnHeight={45}
                        btnFontWeight={600}
                        btnText="Inbound Requirements"
                        btnArrow={true}
                        btnBg="#fff"
                        btnHoverBg="#f40"
                      />
                    </div>
                    <MediaQuery query="(max-width: 767px)">
                      <FiltersMobileToggler
                        title="Search Filters:"
                        toggleFunc={toggleMobileFiltersHandler}
                      />
                    </MediaQuery>
                  </div>
                }
              />
            ) : (
              <ListSearchMinimalContentLoader />
            )}

            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            {grid.data.init && (
              <List
                items={grid.data.rows}
                itemRender={(item, actions, index) => (
                  <InboundsCandidateItem
                    key={`${item.CandidateID}_${index}`}
                    user={user}
                    item={item}
                    actions={actions}
                    selected={isSelected(item.CandidateID)}
                    footerActions={itemFooterActions}
                  />
                )}
                selectable
                actions={{
                  onSelect: select,
                  onDeselect: deselect,
                  onChangePage: grid.actions.readPage,
                }}
                pagination={{
                  page: grid.data.page,
                  count: grid.data.rowsCount,
                  perPage: grid.data.perPage,
                }}
              />
            )}
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default compose(withSearchActions, withTakeActions)(InBounds);
